/**
 * Palette de couverture d'un projet.
 *
 * Note sur la règle « pas de hex en dur » (cf. 04-conventions.md) : les
 * couleurs produites ici sont des *données*, pas des décisions de design —
 * elles varient par projet et ne peuvent donc pas être des classes Tailwind.
 * Les teintes de repli sont choisies pour cohabiter avec l'encre marine du DS
 * (`--ds-color-ink`), et le rendu final passe systématiquement sous un voile
 * d'encre pour rester dans l'univers de la marque.
 */

/* Teintes de repli — voisines de l'encre marine, désaturées, jamais fluo. */
const FALLBACK_HUES = [
  222, // indigo (l'encre elle-même)
  196, // bleu pétrole
  168, // sarcelle
  286, // prune
  24,  // terre brûlée
  142, // forêt
];

/**
 * Hash déterministe (djb2) — le même slug donne toujours le même dégradé,
 * pour qu'un projet garde son identité visuelle d'une page à l'autre.
 */
function hashString(value) {
  let hash = 5381;
  const str = String(value || '');
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) >>> 0;
  }
  return hash;
}

/** #rgb ou #rrggbb → { h, s, l } (h en degrés, s/l en %). Null si invalide. */
function hexToHsl(value) {
  if (typeof value !== 'string') return null;

  let hex = value.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    hex = hex.split('').map((char) => char + char).join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;

  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) return { h: 0, s: 0, l: Math.round(l * 100) };

  const s = delta / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === r)      h = ((g - b) / delta) % 6;
  else if (max === g) h = (b - r) / delta + 2;
  else                h = (r - g) / delta + 4;

  h = Math.round(h * 60);
  if (h < 0) h += 360;

  return { h, s: Math.round(s * 100), l: Math.round(l * 100) };
}

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/**
 * Construit le dégradé d'un projet.
 *
 * @param project { slug, accentColor } — seuls ces deux champs sont lus.
 * @returns { from, to, accent, isFallback }
 *          `isFallback` = true quand aucune accentColor exploitable n'était
 *          fournie et que la teinte a été dérivée du slug.
 */
export function projectPalette(project = {}) {
  const parsed = hexToHsl(project.accentColor);

  const hue = parsed
    ? parsed.h
    : FALLBACK_HUES[hashString(project.slug || project.title) % FALLBACK_HUES.length];

  // Saturation contenue : au-delà, le voile d'encre vire au criard.
  const saturation = parsed ? clamp(parsed.s, 24, 62) : 46;

  return {
    from: `hsl(${hue} ${saturation}% 18%)`,
    to: `hsl(${(hue + 28) % 360} ${clamp(saturation - 8, 18, 55)}% 40%)`,
    accent: `hsl(${hue} ${clamp(saturation + 10, 30, 70)}% 62%)`,
    isFallback: !parsed,
  };
}

/** Initiales affichées en filigrane sur les couvertures sans image (1-2 lettres). */
export function projectInitials(title) {
  const words = String(title || '')
    .trim()
    .split(/[\s—–-]+/)
    .filter(Boolean);

  if (words.length === 0) return '·';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
